/**
 * Step2 字段行相关工具：自定义行表单初值、摘要统计、自定义列名生成。
 */
import { sampleValuesText } from '../components/StepFieldConfig/utils';
import type {
  IFieldConfigRowEdit,
  IStepFieldConfigState,
  IYamlFieldColumn,
  TFieldConfigWizardKind,
} from '../types';

/**
 * 新增自定义维度/指标弹窗的表单草稿初值；
 * 打开内联「新增自定义」表单时重置草稿；`dataType` 随 kind 默认 string/sum。
 */
export const buildEmptyCustomRowEdit = (
  kind: TFieldConfigWizardKind,
): IFieldConfigRowEdit => ({
  displayName: '',
  nameZh: '',
  nameEn: '',
  sql: '',
  description: '',
  dataType: kind === 'dimension' ? 'string' : 'sum',
  sampleValues: '',
  dimSummary: '',
  dimTable: '',
  dimJoinColumn: '',
  commonQuery: '',
  format: '',
  granularity: '',
  filters: '',
});

const hasRequiredText = (value: string): boolean => value.trim().length > 0;

/**
 * JSON 数组字符串（如 `["1","2"]`）解析为数组；非法则返回 null。
 * `buildCubeConfig` 组装 `sample_values` 与 Step2 示例值格式校验共用。
 */
export const parseJsonArrayString = (raw: string): unknown[] | null => {
  const trimmed = raw.trim();
  if (!trimmed.startsWith('[')) {
    return null;
  }
  try {
    const parsed: unknown = JSON.parse(trimmed);
    return Array.isArray(parsed) ? parsed : null;
  } catch {
    return null;
  }
};

/** 维度示例值须为合法 JSON 数组字符串（如 `["255","global"]`） */
export const isDimSampleValuesFormatValid = (raw: string): boolean =>
  parseJsonArrayString(raw) !== null;

// --- Step2 点击「下一步」：表格 / 多选 / 内联 聚合校验（见 useFieldConfigStepValidate） ---

type TFieldConfigBaseRequiredFields = {
  displayName: string;
  nameZh: string;
  nameEn: string;
  dataType: string;
  sql: string;
  description: string;
};

type TDimRequiredFields = TFieldConfigBaseRequiredFields & {
  sampleValues: string;
};

type TMeasRequiredFields = TFieldConfigBaseRequiredFields & {
  commonQuery: string;
};

const isFieldConfigBaseRequiredFilled = (
  fields: TFieldConfigBaseRequiredFields,
): boolean =>
  hasRequiredText(fields.displayName) &&
  hasRequiredText(fields.nameZh) &&
  hasRequiredText(fields.nameEn) &&
  hasRequiredText(fields.dataType) &&
  hasRequiredText(fields.sql) &&
  hasRequiredText(fields.description);

const isDimRequiredFieldsFilled = (fields: TDimRequiredFields): boolean =>
  isFieldConfigBaseRequiredFilled(fields) &&
  hasRequiredText(fields.sampleValues);

const isMeasRequiredFieldsFilled = (fields: TMeasRequiredFields): boolean =>
  isFieldConfigBaseRequiredFilled(fields) &&
  hasRequiredText(fields.commonQuery);

/**
 * 内联新增维度：与表头必填项一致（含示例值）。
 */
export const isDimInlineAddDraftValid = (
  draft: IFieldConfigRowEdit,
): boolean =>
  isDimRequiredFieldsFilled({
    displayName: draft.displayName,
    nameZh: draft.nameZh,
    nameEn: draft.nameEn,
    dataType: draft.dataType,
    sql: draft.sql,
    description: draft.description,
    sampleValues: draft.sampleValues,
  });

/**
 * 内联新增指标：与表头必填项一致（含常见查询问题）。
 */
export const isMeasInlineAddDraftValid = (
  draft: IFieldConfigRowEdit,
): boolean =>
  isMeasRequiredFieldsFilled({
    displayName: draft.displayName,
    nameZh: draft.nameZh,
    nameEn: draft.nameEn,
    dataType: draft.dataType,
    sql: draft.sql,
    description: draft.description,
    commonQuery: draft.commonQuery,
  });

/** 表格内维度行必填（参与 Cube 的列，含示例值） */
export const isDimFieldColumnValid = (col: IYamlFieldColumn): boolean =>
  isDimRequiredFieldsFilled({
    displayName: col.default_display_name || '',
    nameZh: col.default_name_zh || '',
    nameEn: col.default_name_en || '',
    dataType: col.default_type || '',
    sql: col.default_sql || '',
    description: col.default_description || '',
    sampleValues: sampleValuesText(col),
  });

/** 表格内指标行必填 */
export const isMeasFieldColumnValid = (col: IYamlFieldColumn): boolean =>
  isMeasRequiredFieldsFilled({
    displayName: col.default_display_name || '',
    nameZh: col.default_name_zh || '',
    nameEn: col.default_name_en || '',
    dataType: col.default_type || '',
    sql: col.default_sql || '',
    description: col.default_description || '',
    commonQuery: col.default_common_query || '',
  });

/**
 * 已勾选参与 Cube 的列是否均满足必填；未勾选列不参与校验。
 */
export const areSelectedFieldColumnsValid = (
  fieldColumns: IYamlFieldColumn[],
  fieldsValue: string[],
): boolean => {
  if (!fieldsValue.length) {
    return false;
  }
  const selected = new Set(fieldsValue);
  return fieldColumns.every((col) => {
    if (!selected.has(col.source_column)) {
      return true;
    }
    return col.default_kind === 'dimension'
      ? isDimFieldColumnValid(col)
      : isMeasFieldColumnValid(col);
  });
};

/**
 * Step2「下一步」：已选列必填齐全，且未留下未完成的内联新增表单。
 */
export const canProceedFromFieldConfigStep = (
  fieldConfig: Pick<
  IStepFieldConfigState,
  | 'fieldColumns'
  | 'fieldsValue'
  | 'dimInlineOpen'
  | 'measInlineOpen'
  >,
): boolean => {
  // 内联自定义面板打开即视为操作未完成
  if (fieldConfig.dimInlineOpen || fieldConfig.measInlineOpen) {
    return false;
  }
  return areSelectedFieldColumnsValid(
    fieldConfig.fieldColumns,
    fieldConfig.fieldsValue,
  );
};

/** 表格内联编辑单元格字段 key（与列 render 中 `renderTableCell` 第二参一致） */
export type TFieldTableCellKey =
  | 'displayName'
  | 'nameZh'
  | 'nameEn'
  | 'dataType'
  | 'sql'
  | 'description'
  | 'sampleValues'
  | 'commonQuery';

export type TFieldTableRowErrors = Partial<Record<TFieldTableCellKey, boolean>>;

/** `source_column` → 该行需高亮的字段错误 */
export type TFieldTableErrors = Record<string, TFieldTableRowErrors>;

const hasFieldTableRowErrors = (row: TFieldTableRowErrors): boolean =>
  Object.values(row).some(Boolean);

/** 单行维度：必填 + 示例值 JSON 数组格式（与内联新增口径一致） */
const collectDimRowFieldErrors = (col: IYamlFieldColumn): TFieldTableRowErrors => {
  const errors: TFieldTableRowErrors = {};
  if (!hasRequiredText(col.default_display_name || '')) {
    errors.displayName = true;
  }
  if (!hasRequiredText(col.default_name_zh || '')) {
    errors.nameZh = true;
  }
  if (!hasRequiredText(col.default_name_en || '')) {
    errors.nameEn = true;
  }
  if (!hasRequiredText(col.default_type || '')) {
    errors.dataType = true;
  }
  if (!hasRequiredText(col.default_sql || '')) {
    errors.sql = true;
  }
  if (!hasRequiredText(col.default_description || '')) {
    errors.description = true;
  }
  const sample = sampleValuesText(col);
  if (!hasRequiredText(sample)) {
    errors.sampleValues = true;
  } else if (!isDimSampleValuesFormatValid(sample)) {
    errors.sampleValues = true;
  }
  return errors;
};

/** 单行指标：必填（含 common_query） */
const collectMeasRowFieldErrors = (col: IYamlFieldColumn): TFieldTableRowErrors => {
  const errors: TFieldTableRowErrors = {};
  if (!hasRequiredText(col.default_display_name || '')) {
    errors.displayName = true;
  }
  if (!hasRequiredText(col.default_name_zh || '')) {
    errors.nameZh = true;
  }
  if (!hasRequiredText(col.default_name_en || '')) {
    errors.nameEn = true;
  }
  if (!hasRequiredText(col.default_type || '')) {
    errors.dataType = true;
  }
  if (!hasRequiredText(col.default_common_query || '')) {
    errors.commonQuery = true;
  }
  if (!hasRequiredText(col.default_sql || '')) {
    errors.sql = true;
  }
  if (!hasRequiredText(col.default_description || '')) {
    errors.description = true;
  }
  return errors;
};

/**
 * 仅校验已勾选参与 Cube 的行；未勾选列不写入 `cellErrors`。
 * 供 Step2 点击「下一步」与 `useFieldConfigTableColumns` error 高亮共用。
 */
export const collectSelectedFieldTableErrors = (
  fieldColumns: IYamlFieldColumn[],
  fieldsValue: string[],
): TFieldTableErrors => {
  const cellErrors: TFieldTableErrors = {};
  if (!fieldsValue.length) {
    return cellErrors;
  }
  const selected = new Set(fieldsValue);
  for (const col of fieldColumns) {
    if (!selected.has(col.source_column)) {
      continue;
    }
    const rowErrors =
      col.default_kind === 'dimension'
        ? collectDimRowFieldErrors(col)
        : collectMeasRowFieldErrors(col);
    if (hasFieldTableRowErrors(rowErrors)) {
      cellErrors[col.source_column] = rowErrors;
    }
  }
  return cellErrors;
};

export const hasFieldTableErrors = (errors: TFieldTableErrors): boolean =>
  Object.keys(errors).length > 0;

/** `validateFieldConfigStepOnConfirm` 返回值，供 hook 映射为各 UI error 态 */
export interface IFieldConfigStepConfirmResult {
  valid: boolean;
  /** 表格单元格级错误，key 为 `source_column` */
  cellErrors: TFieldTableErrors;
  noFieldsSelected: boolean;
  /** 内联新增维度面板仍打开（自定义操作未完成） */
  dimSecError: boolean;
  /** 内联新增指标面板仍打开 */
  measSecError: boolean;
}

type TFieldConfigStepConfirmInput = Pick<
IStepFieldConfigState,
| 'fieldColumns'
| 'fieldsValue'
| 'dimInlineOpen'
| 'measInlineOpen'
>;

/** Step2 点击「下一步」时校验（仅用于展示 error，不控制按钮 disabled） */
export const validateFieldConfigStepOnConfirm = (
  fieldConfig: TFieldConfigStepConfirmInput,
): IFieldConfigStepConfirmResult => {
  const noFieldsSelected = !fieldConfig.fieldsValue.length;
  const dimSecError = fieldConfig.dimInlineOpen;
  const measSecError = fieldConfig.measInlineOpen;
  const cellErrors = collectSelectedFieldTableErrors(
    fieldConfig.fieldColumns,
    fieldConfig.fieldsValue,
  );
  const valid =
    !noFieldsSelected &&
    !dimSecError &&
    !measSecError &&
    !hasFieldTableErrors(cellErrors);
  return {
    valid,
    cellErrors,
    noFieldsSelected,
    dimSecError,
    measSecError,
  };
};

/** 统计 `fieldColumns` 中维度 / 指标数量（摘要卡片，与 buildCubeConfig 口径一致） */
export const countFieldKinds = (
  fieldConfig: IStepFieldConfigState,
): { dimCount: number; measCount: number; } => {
  let dimCount = 0;
  let measCount = 0;
  for (const col of fieldConfig.fieldColumns) {
    if (col.default_kind === 'dimension') {
      dimCount += 1;
    } else {
      measCount += 1;
    }
  }
  return { dimCount, measCount };
};

/** 为自定义行生成不重复的 `source_column` / cube 名：`custom_dim_n` 或 `custom_meas_n` */
export const makeCustomCubeFieldName = (
  kind: TFieldConfigWizardKind,
  existing: IYamlFieldColumn[],
): string => {
  const prefix = kind === 'dimension' ? 'custom_dim' : 'custom_meas';
  const n =
    existing.filter(
      (c) =>
        Boolean(c.is_custom) &&
        (kind === 'dimension'
          ? c.default_kind === 'dimension'
          : c.default_kind === 'measure'),
    ).length + 1;
  return `${prefix}_${n}`;
};
